/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Button } from 'antd'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { FieldArray, useFormikContext } from 'formik'
import colors from '@sportninja/common/constants/appColors'
import InlineTextButton from 'src/components/ActionButton/InlineTextButton'
import { paragraphMedium, paragraphXSmall } from 'src/components/css'
import { MODAL_TOKENS } from 'src/styles/modalTokens'
import {
  FieldRow,
  TextField,
} from 'src/features/profile/player/setupModal/components/FormikField'
import { balanceLabel, balanceText } from '../utils/balanceI18n'
import {
  EMAIL_MAX_LENGTH,
  emptyInviteRow,
  MAX_INVITEES,
  NAME_MAX_LENGTH,
} from '../utils/inviteForm'
import type { InviteFormValues } from '../types/slotInvite'

/** Below this list width a person's three fields stack instead of sharing a line. */
const INVITEE_FIELDS_COLLAPSE_AT = 440
/** The fields' columns — the header row repeats them so each name sits over its column. */
const FIELD_COLUMNS = '1fr 1fr 1.4fr'
/** antd's text Button is 32px square; the header reserves the same beside its names. */
const REMOVE_WIDTH = 32

/**
 * Add Player Slots › Reserved Player Slots — one row per player, up to
 * MAX_INVITEES, each becoming its own slot held for them.
 *
 * The columns are named once, above the rows (Inon, Oct 2): repeating "First
 * Name / Last Name / Email" over every person was most of the form. Each input
 * keeps its name for assistive tech. On a phone the fields stack, the shared
 * names no longer sit over anything, and the placeholders name each field.
 */
const InviteeRows = () => {
  const { values } = useFormikContext<InviteFormValues>()
  const count = values.invitees.length
  const removable = count > 1

  return (
    <FieldArray name='invitees'>
      {(rows) => (
        <div css={rowsStyle}>
          <div css={headerStyle} aria-hidden='true'>
            <div css={headerNamesStyle}>
              <span>{balanceLabel('common:balanceInviteFirstName')}</span>
              <span>{balanceLabel('common:balanceInviteLastName')}</span>
              <span>{balanceLabel('common:balanceInviteEmail')}</span>
            </div>
            {removable ? <span css={removeSpacerStyle} /> : null}
          </div>

          {values.invitees.map((_, index) => (
            <div
              // Formik keys each field by its row index, so the index is its identity.
              key={index}
              css={inviteeRowStyle}
              data-testid={`invitee-row-${index}`}
            >
              <div css={inviteeFieldsStyle}>
                <FieldRow
                  template={FIELD_COLUMNS}
                  collapseAt={INVITEE_FIELDS_COLLAPSE_AT}
                >
                  <TextField
                    name={`invitees.${index}.name_first`}
                    label={balanceLabel('common:balanceInviteFirstName')}
                    placeholder={balanceLabel(
                      'common:balanceInviteFirstNamePlaceholder'
                    )}
                    maxLength={NAME_MAX_LENGTH}
                    hideLabel
                    size='large'
                  />
                  <TextField
                    name={`invitees.${index}.name_last`}
                    label={balanceLabel('common:balanceInviteLastName')}
                    placeholder={balanceLabel(
                      'common:balanceInviteLastNamePlaceholder'
                    )}
                    maxLength={NAME_MAX_LENGTH}
                    hideLabel
                    size='large'
                  />
                  <TextField
                    name={`invitees.${index}.email`}
                    label={balanceLabel('common:balanceInviteEmail')}
                    placeholder={balanceLabel(
                      'common:balanceInviteEmailPlaceholder'
                    )}
                    maxLength={EMAIL_MAX_LENGTH}
                    hideLabel
                    size='large'
                  />
                </FieldRow>
              </div>
              {removable ? (
                <Button
                  type='text'
                  icon={<CloseOutlined />}
                  aria-label={balanceText('common:balanceInviteRemoveRow', {
                    number: index + 1,
                  })}
                  onClick={() => rows.remove(index)}
                  css={removeStyle}
                  data-testid={`invitee-remove-${index}`}
                />
              ) : null}
            </div>
          ))}

          {count < MAX_INVITEES ? (
            <div>
              <InlineTextButton
                text={balanceLabel('common:balanceInviteAddAnother')}
                tone='primary'
                icon={<PlusOutlined />}
                onClick={() => rows.push(emptyInviteRow())}
                data-testid='invitee-add'
              />
            </div>
          ) : (
            <p css={hintStyle}>
              {balanceText('common:balanceInviteMaxReached', {
                max: MAX_INVITEES,
              })}
            </p>
          )}
        </div>
      )}
    </FieldArray>
  )
}

export default InviteeRows

const rowsStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* FieldRow collapses on its CONTAINER's width, not the viewport's: the list of
     rows is that container. */
  container-type: inline-size;
`
const headerStyle = css`
  display: flex;
  gap: 8px;

  /* Stacked fields have no columns to name. */
  @container (max-width: ${INVITEE_FIELDS_COLLAPSE_AT}px) {
    display: none;
  }
`
/** The same grid as each row's FieldRow, so every name sits over its column. */
const headerNamesStyle = css`
  ${paragraphMedium};
  flex: 1 1 auto;
  min-width: 0;
  display: grid;
  grid-template-columns: ${FIELD_COLUMNS};
  gap: 16px;
  color: ${colors.WHITE};
`
const removeSpacerStyle = css`
  flex: 0 0 ${REMOVE_WIDTH}px;
`
/** The fields take the row; the remove control sits beside them, level with the inputs. */
const inviteeRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 8px;

  /* Stacked (a phone), one person's fields sit further apart than two people do:
     rule each person off from the one above. */
  @container (max-width: ${INVITEE_FIELDS_COLLAPSE_AT}px) {
    & + & {
      padding-top: 12px;
      border-top: 1px solid ${MODAL_TOKENS.BORDER_COLOR};
    }
  }
`
const inviteeFieldsStyle = css`
  flex: 1 1 auto;
  min-width: 0;
`
/** Centred on its 40px input. */
const removeStyle = css`
  margin-top: 4px;
  flex-shrink: 0;
  color: ${colors.NEUTRAL_50};
`
const hintStyle = css`
  ${paragraphXSmall};
  margin: 0;
  color: ${colors.NEUTRAL_50};
`
